import type {
  QuickFillSources,
  RotaStatus,
  RotaWeek,
  SaveException,
  SavePattern,
  TeamCard,
  WeeklyPattern,
} from "@dentacore/shared-types";

import { api } from "./client";

/**
 * The rota — Phase 9.
 *
 * **Branch-scoped**, because a pattern is: `staff_patterns` carries a branch
 * while staff are shared across a clinic's branches (S2). **Every role reads
 * it**, unlike Settings — a dentist needs to see their own week.
 */
export function readPattern(userId: string, signal?: AbortSignal): Promise<WeeklyPattern> {
  return api<WeeklyPattern>(`/staff/${userId}/pattern`, { signal, branchScoped: true });
}

/** Save a new version. The one in force is closed on the same date (S1). */
export function savePattern(userId: string, body: SavePattern): Promise<WeeklyPattern> {
  return api<WeeklyPattern>(`/staff/${userId}/pattern`, {
    method: "PUT",
    body,
    branchScoped: true,
  });
}

/** Undo the last save by reopening the version before it. */
export function revertPattern(userId: string): Promise<WeeklyPattern> {
  return api<WeeklyPattern>(`/staff/${userId}/pattern/revert`, {
    method: "POST",
    branchScoped: true,
  });
}

/**
 * What a week can be filled from — STORY-091, decisions S3 and S4.
 *
 * **A read, and only a read.** Every save opens a version (S1), so filling is
 * something the editor does to the unsaved form; Save and Discard stay the only
 * writers, which is what makes trying a fill costless.
 */
export function readQuickFill(userId: string, signal?: AbortSignal): Promise<QuickFillSources> {
  return api<QuickFillSources>(`/staff/${userId}/pattern/quick-fill`, {
    signal,
    branchScoped: true,
  });
}

/**
 * Seven dated days at this branch — STORY-092.
 *
 * **The effective week, not the pattern**: §6.4's exception beats the pattern
 * for its date, and each day says which level decided it.
 */
export function readWeek(
  userId: string,
  weekStart: string,
  signal?: AbortSignal,
  /** Another of their locations than the active one (HR-D19). */
  branchId?: string,
): Promise<RotaWeek> {
  const query = new URLSearchParams({ weekStart });
  return api<RotaWeek>(`/staff/${userId}/week?${query.toString()}`, {
    signal,
    branchScoped: true,
    ...(branchId === undefined ? {} : { branchId }),
  });
}

/**
 * Everybody's dated week at this branch, at once (ST6) — what the Staff
 * screen's day counts and week squares are drawn from. Each is the same week
 * `readWeek` returns for that person.
 */
export function readWeeks(weekStart: string, signal?: AbortSignal): Promise<RotaWeek[]> {
  const query = new URLSearchParams({ weekStart });
  return api<RotaWeek[]>(`/staff/week?${query.toString()}`, { signal, branchScoped: true });
}

/** Override one date. An upsert — editing one is the same gesture as making it. */
export function setException(userId: string, date: string, body: SaveException): Promise<RotaWeek> {
  return api<RotaWeek>(`/staff/${userId}/exceptions/${date}`, {
    method: "PUT",
    body,
    branchScoped: true,
  });
}

/** Put one date back on the pattern. Deleting the override *is* restoring it. */
export function clearException(userId: string, date: string): Promise<RotaWeek> {
  return api<RotaWeek>(`/staff/${userId}/exceptions/${date}`, {
    method: "DELETE",
    branchScoped: true,
  });
}

/**
 * Put one week back on the regular hours — STORY-094.
 *
 * **This person's week only**, and only that week: an override next Tuesday
 * survives clearing this one, which is the difference between an undo and a
 * purge.
 */
export function clearWeek(userId: string, weekStart: string): Promise<RotaWeek> {
  const query = new URLSearchParams({ weekStart });
  return api<RotaWeek>(`/staff/${userId}/week?${query.toString()}`, {
    method: "DELETE",
    branchScoped: true,
  });
}

/**
 * Make one date's hours the regular ones for that weekday — STORY-093.
 *
 * **The exception is kept, not cleared** (S5): it agrees with the new pattern,
 * so the day reads the same either way, and keeping it is what makes reverting
 * the pattern an exact undo rather than a second change.
 */
export function promoteException(userId: string, date: string): Promise<RotaWeek> {
  return api<RotaWeek>(`/staff/${userId}/exceptions/${date}/promote`, {
    method: "POST",
    branchScoped: true,
  });
}

/**
 * Publish this branch's week — STORY-095.
 *
 * **Branch-scoped and not per person**: publishing a rota publishes the whole
 * location's week, which is what makes it a decision rather than seven of them.
 */
export function publishWeek(weekStart: string): Promise<RotaStatus> {
  const query = new URLSearchParams({ weekStart });
  return api<RotaStatus>(`/staff/week/publish?${query.toString()}`, {
    method: "POST",
    branchScoped: true,
  });
}

/** Take it back to draft. Deleting the row is what unpublishing is. */
export function unpublishWeek(weekStart: string): Promise<RotaStatus> {
  const query = new URLSearchParams({ weekStart });
  return api<RotaStatus>(`/staff/week/publish?${query.toString()}`, {
    method: "DELETE",
    branchScoped: true,
  });
}

/**
 * The roster at a glance — STORY-099.
 *
 * **Branch-scoped**, because the load bar is this location's hours (S11) — the
 * clinic-wide total comes back beside it rather than instead of it.
 */
export function listTeam(signal?: AbortSignal): Promise<TeamCard[]> {
  return api<TeamCard[]>("/staff/team", { signal, branchScoped: true });
}
